import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Lock, Send, UserPlus, ShieldCheck, Users, Trash2, KeyRound, AlertCircle, Video, CalendarDays, Plus, ExternalLink, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  deriveLabKey, verifyProbe, encryptText, decryptText, getCachedLabKey, cacheLabKey,
} from "@/lib/crypto";

export default function LabDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lab, setLab] = useState(null);
  const [messages, setMessages] = useState([]);
  const [decrypted, setDecrypted] = useState({});
  const [key, setKey] = useState(getCachedLabKey(id) || null);
  const [passphrase, setPassphrase] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meeting, setMeeting] = useState({ title: "", url: "https://meet.new", scheduled_at: "" });
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: l }, { data: msgs }, { data: mts }] = await Promise.all([
      api.get(`/labs/${id}`),
      api.get(`/labs/${id}/messages`),
      api.get(`/labs/${id}/meetings`),
    ]);
    setLab(l);
    setMessages(msgs);
    setMeetings(mts);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Decrypt messages when key changes
  useEffect(() => {
    if (!key) { setDecrypted({}); return; }
    (async () => {
      const next = {};
      for (const m of messages) {
        next[m.id] = (await decryptText(key, m.iv, m.ciphertext)) || "⚠️ unable to decrypt";
      }
      setDecrypted(next);
      setTimeout(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, 100);
    })();
  }, [key, messages]);

  // Poll for new messages — only while tab is visible, every 30s
  useEffect(() => {
    let t;
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const start = () => {
      clearInterval(t);
      t = setInterval(tick, 30000);
    };
    start();
    document.addEventListener("visibilitychange", start);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", start);
    };
  }, [load]);

  const unlock = async (e) => {
    e?.preventDefault?.();
    if (!passphrase) return;
    setUnlocking(true);
    try {
      const ok = await verifyProbe(passphrase, lab.probe);
      if (!ok) { toast.error("Wrong passphrase"); return; }
      const k = await deriveLabKey(passphrase, lab.probe.salt);
      cacheLabKey(id, k);
      setKey(k);
      setPassphrase("");
      toast.success("Lab unlocked");
    } finally { setUnlocking(false); }
  };

  const send = async () => {
    if (!draft.trim() || !key) return;
    setSending(true);
    try {
      const { iv, ciphertext } = await encryptText(key, draft);
      await api.post(`/labs/${id}/messages`, { iv, ciphertext });
      setDraft("");
      load();
    } catch {
      toast.error("Failed to send");
    } finally { setSending(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Email required");
    try {
      await api.post(`/labs/${id}/invites`, { email: inviteEmail.trim().toLowerCase() });
      toast.success("Member added. Share the passphrase with them via a secure channel.");
      setInviteEmail("");
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/labs/${id}/members/${memberId}`);
    toast.success("Removed");
    load();
  };

  const scheduleMeeting = async () => {
    if (!meeting.title.trim() || !meeting.scheduled_at) return toast.error("Title and time required");
    if (!meeting.url.startsWith("http")) return toast.error("Meeting URL must start with http(s)://");
    try {
      await api.post(`/labs/${id}/meetings`, {
        title: meeting.title,
        url: meeting.url,
        scheduled_at: new Date(meeting.scheduled_at).toISOString(),
      });
      toast.success("Meeting scheduled — members notified");
      setMeetingOpen(false);
      setMeeting({ title: "", url: "https://meet.new", scheduled_at: "" });
      load();
    } catch {
      toast.error("Failed to schedule");
    }
  };

  const deleteMeeting = async (mid) => {
    if (!window.confirm("Delete this meeting?")) return;
    await api.delete(`/labs/${id}/meetings/${mid}`);
    load();
  };

  if (!lab) return <div className="p-8 text-sm font-mono text-zinc-500">loading…</div>;

  const isSupervisor = lab.role === "supervisor";

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="lab-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/labs" className="text-sm font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> All labs
        </Link>
        <div className="flex items-center gap-2">
          {isSupervisor && (
            <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="schedule-meet-btn"><Video className="h-4 w-4 mr-2" />Schedule Meet</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Schedule a Google Meet</DialogTitle>
                  <DialogDescription>
                    Paste a meeting link or use <code className="font-mono text-xs">https://meet.new</code> for an
                    instant Google Meet. All members of this lab will be notified.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Title</Label>
                    <Input value={meeting.title} onChange={(e) => setMeeting({ ...meeting, title: e.target.value })} placeholder="Weekly lab sync" data-testid="meeting-title-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Date & time</Label>
                    <Input type="datetime-local" value={meeting.scheduled_at} onChange={(e) => setMeeting({ ...meeting, scheduled_at: e.target.value })} data-testid="meeting-time-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Meeting URL</Label>
                    <Input value={meeting.url} onChange={(e) => setMeeting({ ...meeting, url: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij" data-testid="meeting-url-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMeetingOpen(false)}>Cancel</Button>
                  <Button onClick={scheduleMeeting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-meeting-btn">Schedule</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isSupervisor && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="invite-member-btn"><UserPlus className="h-4 w-4 mr-2" />Add member</Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a lab member</DialogTitle>
              <DialogDescription>
                Add a researcher by email. They will be added to the lab automatically the next time
                they sign in. Share the passphrase with them via a secure out-of-band channel.
              </DialogDescription>
            </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Email</Label>
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="member@university.edu" data-testid="invite-email-input" />
                </div>
                <div className="flex items-start gap-2 text-xs font-mono text-zinc-500 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded p-3">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-blue-600" />
                  Share the lab passphrase with the invited member through a secure, out-of-band channel
                  (in person, secure chat, etc). ResyNote cannot share it for you.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button onClick={invite} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-invite-btn">Add member</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight">{lab.name}</h1>
          {lab.description && <p className="text-sm font-body text-zinc-500 mt-2">{lab.description}</p>}
          <div className="flex items-center gap-3 text-xs font-mono text-zinc-500 mt-3">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> end-to-end encrypted
            </span>
            <span>·</span>
            <span>{lab.members?.length || 0} member{(lab.members?.length || 0) !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Feed */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md flex flex-col h-[70vh]">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-blue-600" />
              <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                Encrypted feed
              </h2>
            </div>
            {key ? (
              <span className="text-xs font-mono text-emerald-600 inline-flex items-center gap-1">
                <KeyRound className="h-3 w-3" /> unlocked
              </span>
            ) : (
              <span className="text-xs font-mono text-zinc-500">locked</span>
            )}
          </div>

          {!key ? (
            <form onSubmit={unlock} className="flex-1 flex items-center justify-center p-8">
              <div className="max-w-sm w-full space-y-4 text-center">
                <Lock className="h-8 w-8 mx-auto text-zinc-400" />
                <div>
                  <div className="font-sans font-semibold">Lab is encrypted</div>
                  <div className="text-sm font-body text-zinc-500 mt-1">
                    Enter the passphrase shared by your supervisor to view and post updates.
                  </div>
                </div>
                <Input
                  type="password"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Passphrase"
                  data-testid="lab-unlock-input"
                  autoFocus
                />
                <Button type="submit" disabled={unlocking} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="lab-unlock-btn">
                  {unlocking ? "Unlocking…" : "Unlock lab"}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-10 text-sm font-mono text-zinc-500">no messages yet</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`lab-msg-${m.id}`}>
                        <div className={`max-w-[75%] rounded-md p-3 ${mine ? "bg-blue-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"}`}>
                          {!mine && (
                            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
                              {m.sender_name || m.sender_email}
                            </div>
                          )}
                          <div className="font-body text-sm whitespace-pre-wrap break-words">{decrypted[m.id] ?? "…"}</div>
                          {m.experiment_id && (
                            <Link
                              to={`/experiments/${m.experiment_id}`}
                              className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded px-2 py-1 ${mine ? "bg-blue-500/40 hover:bg-blue-500/60 text-blue-50" : "bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:hover:bg-blue-950 dark:text-blue-300"}`}
                              data-testid={`msg-exp-link-${m.id}`}
                            >
                              <FlaskConical className="h-3 w-3" /> linked experiment
                            </Link>
                          )}
                          <div className={`text-[10px] font-mono mt-1 ${mine ? "text-blue-100" : "text-zinc-500"}`}>
                            {new Date(m.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-zinc-200 dark:border-zinc-800 p-3 flex gap-2">
                <Textarea
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Share an update… (encrypted locally)"
                  className="resize-none min-h-0 h-10 py-2"
                  data-testid="lab-msg-input"
                />
                <Button onClick={send} disabled={sending || !draft.trim()} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" data-testid="lab-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Members */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">Members</h2>
          </div>
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {(lab.members || []).map((m) => (
              <li key={m.id} className="p-4 flex items-center gap-3" data-testid={`member-${m.id}`}>
                <div className="h-8 w-8 rounded-full bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-sm font-semibold">
                  {(m.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm truncate">{m.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {m.role} · {m.status}
                  </div>
                </div>
                {isSupervisor && m.role !== "supervisor" && (
                  <Button variant="ghost" size="icon" onClick={() => removeMember(m.id)} className="hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {/* Meetings */}
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
            <Video className="h-4 w-4 text-blue-600" />
            <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">Meetings</h2>
          </div>
          {meetings.length === 0 ? (
            <div className="p-4 text-xs font-mono text-zinc-500 text-center">no meetings scheduled</div>
          ) : (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {meetings.map((m) => (
                <li key={m.id} className="p-4 flex items-start gap-3" data-testid={`meeting-${m.id}`}>
                  <CalendarDays className="h-4 w-4 text-blue-600 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm font-medium truncate">{m.title}</div>
                    <div className="text-xs font-mono text-zinc-500">{new Date(m.scheduled_at).toLocaleString()}</div>
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                      <ExternalLink className="h-3 w-3" /> Join meeting
                    </a>
                  </div>
                  {isSupervisor && (
                    <Button variant="ghost" size="icon" onClick={() => deleteMeeting(m.id)} className="hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
